import { useEffect, useRef, useState, useCallback } from 'react';
import { OpenAIRealtimeManager } from '../lib/OpenAIRealtime';
import { Mic } from 'lucide-react';

// Define Recording State Enum
enum RecordingState {
    Idle,
    Connecting, // Added state for connection process
    Recording,
    Processing // Internal state after stopping recording, before disconnected
}

export function VoiceChat() {
    // Refactored State
    const [recordingState, setRecordingState] = useState<RecordingState>(RecordingState.Idle);
    const [error, setError] = useState<string | null>(null);
    // Revert transcript state back to simple strings
    const [currentTranscript, setCurrentTranscript] = useState<string>('');
    const [finalTranscript, setFinalTranscript] = useState<string>('');
    // Persistent User ID for memory across sessions
    const [persistentUserId, setPersistentUserId] = useState<string | null>(null);
    const [isUserSpeaking, setIsUserSpeaking] = useState<boolean>(false); // Keep for UI feedback
    
    // Manager ref is now just a container, instance created on demand
    const rtcManagerRef = useRef<OpenAIRealtimeManager | null>(null);
    const audioPlayerRef = useRef<HTMLDivElement>(null); 
    // Keep transcriptEndRef for scrolling the simpler view if needed
    const transcriptEndRef = useRef<HTMLDivElement>(null); 
    // Ref to store the initial agent context
    const initialAgentContextRef = useRef<string>("You are Zen Baptiste, an empathetic coach using the GROW model. Keep responses concise.");
    // Ref to store the latest user transcript for recall context
    const latestUserTranscriptRef = useRef<string>("");

    // --- Event Handlers from Manager --- 
    const handleManagerConnected = useCallback(async () => { // Make async
        console.log('[VoiceChat] Manager Connected. Preparing initial context...');
        // Don't transition state yet, wait for context update
        // setRecordingState(RecordingState.Recording);
        setError(null); 
        setFinalTranscript(''); 
        setCurrentTranscript('');
        latestUserTranscriptRef.current = ""; 
        
        const manager = rtcManagerRef.current;
        const audioElement = manager?.getAudioElement();
        if (audioElement && audioPlayerRef.current) {
            audioPlayerRef.current.innerHTML = ''; 
            audioPlayerRef.current.appendChild(audioElement);
        }

        // --- Retrieve Memory and Set Initial Context --- 
        const userId = persistentUserId;
        let initialContext = initialAgentContextRef.current; // Start with default

        if (manager && userId) {
            console.log(`[VoiceChat] Retrieving initial memory for user ${userId}...`);
            try {
                const workerBaseUrl = import.meta.env.VITE_WORKER_BASE_URL;
                 // Use a generic context for initial recall
                const recallContext = "Initial conversation start"; 
                const response = await fetch(`${workerBaseUrl}/api/memory/retrieve?userId=${userId}&context=${encodeURIComponent(recallContext)}`);

                if (!response.ok) {
                    console.warn('[VoiceChat] Failed to retrieve initial memories:', await response.text());
                } else {
                    const recalledSummaries: string[] = await response.json();
                    console.log('[VoiceChat] Received initial memories:', recalledSummaries);
                    if (recalledSummaries.length > 0) {
                        const memoryContext = "Remember this relevant context from previous conversations:\n" + 
                                            recalledSummaries.map(s => `- ${s}`).join('\n');
                        initialContext = `${initialAgentContextRef.current}\n\n${memoryContext}`; 
                    }
                }
            } catch (err) {
                 console.error('[VoiceChat] Error retrieving initial memory:', err);
                 // Proceed with default context if recall fails
            }

            // Set the potentially combined context
            console.log('[VoiceChat] Setting final initial agent context:', initialContext);
            manager.updateSession({ instructions: initialContext });
            
            // TODO: Ideally wait for session.updated event here before proceeding
            // For now, assume update happens quickly and transition state
             console.log('[VoiceChat] Context set, transitioning to Recording state.');
            setRecordingState(RecordingState.Recording); 

        } else {
             console.warn('[VoiceChat] Manager or UserID not available, cannot set initial context.');
             // Transition state even if context setting fails
             setRecordingState(RecordingState.Recording);
        }
    }, [persistentUserId]); // Add persistentUserId dependency

    const handleManagerDisconnected = useCallback(() => {
        console.log('[VoiceChat] Manager Disconnected. Cleaning up.');
        setCurrentTranscript('');
        latestUserTranscriptRef.current = "";
        if (audioPlayerRef.current) {
            audioPlayerRef.current.innerHTML = '';
        }
        // Clear the manager instance ref
        rtcManagerRef.current = null;
    }, []);

    const handleManagerError = useCallback((err: Error) => {
        console.error('[VoiceChat] Manager Error:', err);
        setError(err.message || 'An unknown connection error occurred');
        setRecordingState(RecordingState.Idle); 
        latestUserTranscriptRef.current = ""; 
        // Also clear the manager instance on error
        rtcManagerRef.current = null; 
    }, []);

    const handleTranscriptDelta = useCallback((delta: string) => {
        setCurrentTranscript(prev => prev + delta);
    }, []);

    const handleTranscriptDone = useCallback(async (finalText: string) => {
        if (!finalText.trim()) return;
        
        const speaker = 'ai';
        const aiResponseText = finalText.trim();

        setFinalTranscript(prev => prev + (prev ? '\n' : '') + `AI: ${aiResponseText}`);

        // Store AI turn (no changes needed here)
        const userId = persistentUserId;
        if (userId) {
           try {
               const workerBaseUrl = import.meta.env.VITE_WORKER_BASE_URL;
               console.log('[VoiceChat] Storing AI turn memory for userId:', userId);
               await fetch(`${workerBaseUrl}/api/memory/add`, {
                   method: 'POST',
                   headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify({ userId, speaker, text: aiResponseText })
               });
           } catch (memError) {
               console.error('[VoiceChat] Failed to store AI turn memory:', memError);
           }
        }
        setCurrentTranscript('');
        
    }, [persistentUserId]);

    // Handle user speech start/stop for state changes
    const handleSpeechStarted = useCallback(() => {
        console.log('[VoiceChat] User Speech Started');
        setIsUserSpeaking(true);
        setCurrentTranscript(''); 
        latestUserTranscriptRef.current = ""; // Clear previous user transcript
    }, []);

    const handleSpeechStopped = useCallback(() => {
        console.log('[VoiceChat] User Speech Stopped');
        setIsUserSpeaking(false);
        // *** Recall now happens on connect, just request response ***
        console.log('[VoiceChat] Requesting model response after user speech stopped.');
        rtcManagerRef.current?.requestModelResponse();
    }, []); // No dependencies needed now

    // --- Initialization: Only Get Persistent ID --- 
    useEffect(() => {
        let userId = localStorage.getItem('persistentUserId');
        if (!userId) {
            userId = crypto.randomUUID();
            localStorage.setItem('persistentUserId', userId);
            console.log('[VoiceChat] Generated new persistentUserId:', userId);
        } else {
            console.log('[VoiceChat] Found existing persistentUserId:', userId);
        }
        setPersistentUserId(userId);

        // No manager initialization here anymore

        // Cleanup function for component unmount
        return () => {
            console.log('[VoiceChat] Cleaning up component, disconnecting any active manager...');
            rtcManagerRef.current?.disconnect(); // Disconnect if exists
            rtcManagerRef.current = null; // Clear ref on unmount
        };
    }, []); // Runs only once on mount to get/set persistent ID
    
    // Scroll transcript area to bottom
    useEffect(() => {
        transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [finalTranscript, currentTranscript]);

    // --- Mic Button Logic --- 
    const handleMicToggle = useCallback(async () => {
        // Get existing manager or null
        let manager = rtcManagerRef.current;

        if (recordingState === RecordingState.Idle) {
            // --- STARTING --- 
            console.log('UI: Mic toggled - Starting connection...');
            setRecordingState(RecordingState.Connecting);
            setError(null);
            setFinalTranscript(''); 
            setCurrentTranscript('');
            latestUserTranscriptRef.current = "";

            // Create NEW manager instance
            const workerBaseUrl = import.meta.env.VITE_WORKER_BASE_URL;
            if (!workerBaseUrl) {
                 setError('Worker URL not configured.'); 
                 setRecordingState(RecordingState.Idle);
                 return; 
            }
            try {
                manager = new OpenAIRealtimeManager(workerBaseUrl);
                rtcManagerRef.current = manager; // Store ref to the new manager

                // Attach listeners to the NEW manager
                manager.on('connected', handleManagerConnected);
                manager.on('disconnected', handleManagerDisconnected);
                manager.on('error', handleManagerError);
                manager.on('transcriptDelta', handleTranscriptDelta);
                manager.on('transcriptDone', handleTranscriptDone);
                manager.on('speechStarted', handleSpeechStarted); 
                manager.on('speechStopped', handleSpeechStopped);

                // Now connect
                await manager.connect();
                // State transitions handled by events
            } catch (err) {
                console.error('UI: Connect or Init failed:', err);
                setError(err instanceof Error ? err.message : 'Failed to initialize connection');
                setRecordingState(RecordingState.Idle);
                rtcManagerRef.current = null; // Clear ref on init failure
            }
        } else if (recordingState === RecordingState.Recording && manager) {
            // --- STOPPING --- 
             console.log('UI: Mic toggled - Stopping connection...');
            setRecordingState(RecordingState.Processing);
            try {
                await manager.disconnect();
                // Explicitly set back to Idle after disconnect attempt completes
                console.log('[VoiceChat] Disconnect call finished, setting state to Idle.');
                setRecordingState(RecordingState.Idle);
            } catch (err) {
                 console.error('UI: Disconnect failed:', err);
                 setError(err instanceof Error ? err.message : 'Failed to disconnect gracefully');
                 setRecordingState(RecordingState.Idle); // Force back to Idle on disconnect error
            }
        }
        // Ignore clicks in Connecting/Processing states
    }, [recordingState, persistentUserId, handleManagerConnected, handleManagerDisconnected, handleManagerError, handleTranscriptDelta, handleTranscriptDone, handleSpeechStarted, handleSpeechStopped]); // Added persistentUserId

    // Determine Mic Button Style and Label
    let micButtonStyle = 'bg-blue-500 hover:bg-blue-600';
    let micButtonLabel = 'Start talking';
    let isMicDisabled = false;

    switch (recordingState) {
        case RecordingState.Connecting:
            micButtonStyle = 'bg-yellow-500'; // Keep yellow, remove pulse for connecting?
            micButtonLabel = 'Connecting...';
            isMicDisabled = true;
            break;
        case RecordingState.Recording:
            micButtonStyle = 'bg-red-500 hover:bg-red-600'; // Remove animate-pulse
            micButtonLabel = 'Stop';
            break;
        case RecordingState.Processing:
            micButtonStyle = 'bg-gray-400 animate-pulse'; // Maybe pulse during processing?
            micButtonLabel = 'Processing...';
            isMicDisabled = true;
            break;
        case RecordingState.Idle:
        default:
            // Styles already set
            break;
    }

    return (
        // Use min-h-full and flex properties for vertical centering within its container
        <div className="p-6 max-w-md mx-auto flex flex-col items-center justify-center text-center min-h-[300px] h-full">
            
            {/* Header & Subtext */}
            {/* Style Guide: H1Bold */}
            <h2 className="text-2xl font-bold text-black mb-2">🔥 What's on your mind?</h2>
            {/* Style Guide: BodyRegular */}
            <p className="text-base font-normal text-gray-600 mb-10">
                Tap the mic and tell me about a challenge you're facing.
            </p>
            
            {/* Mic Button Area */}
            <div className="flex flex-col items-center mb-10"> {/* Adjusted margin */}
                <button
                    onClick={handleMicToggle}
                    disabled={isMicDisabled}
                    className={`w-20 h-20 rounded-full flex items-center justify-center text-white shadow-lg transition-colors duration-200 ${micButtonStyle} disabled:opacity-50 disabled:cursor-not-allowed`}
                    aria-label={micButtonLabel}
                >
                    <Mic size={32} />
                </button>
                {/* Style Guide: LabelBold */}
                <p className="mt-4 text-base font-bold text-black">
                    {micButtonLabel}
                </p>
            </div>

            {/* Transcript Area - Reverted Rendering */} 
            <div className="flex-grow w-full p-4 border rounded bg-gray-50 text-left min-h-[100px] max-h-[300px] overflow-y-auto relative">
                {(recordingState === RecordingState.Idle || recordingState === RecordingState.Connecting) && !finalTranscript && (
                    <p className="text-sm text-gray-500 italic text-center">Your conversation will appear here...</p>
                )}
                {/* Display combined final transcript */} 
                <pre className="whitespace-pre-wrap text-base font-normal text-gray-800">{finalTranscript}</pre>
                {/* Display live delta */} 
                {currentTranscript && (
                    <p className="text-base font-normal text-blue-600 italic animate-pulse">{currentTranscript}</p>
                )}
                 <div ref={transcriptEndRef} /> {/* For auto-scrolling */} 
                 
                 {/* Recall Memory Button & Status - Positioned inside transcript area */}
                 <div className="absolute bottom-2 right-2 flex items-center gap-2">
                    {isUserSpeaking && (<p className="text-xs text-green-600 italic text-center mt-1">Listening...</p>)}
                </div>
            </div>

            {/* Error Display - Placed within the main flex container */} 
            {error && (
                <div className="mt-auto p-3 w-full bg-red-100 border border-red-400 text-red-800 rounded text-sm">
                    <strong>Connection Error:</strong> {error}
                </div>
            )}
            
            {/* Audio Player Container - Kept outside visual flow */}
            <div ref={audioPlayerRef} className="absolute -left-[9999px]">
                {/* Audio element will be appended here */} 
            </div>

        </div> // Closing tag for the main container div
    );
} 